import { ChevronDown, Plus } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { type LeadEmail } from './contactEmails'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface RecipientSelectProps {
  options: LeadEmail[]
  selected: string[]
  onToggle: (email: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

function optionLabel(email: LeadEmail) {
  return email.label && email.label !== email.value ? `${email.label} · ${email.value}` : email.value
}

export function RecipientSelect({
  options,
  selected,
  onToggle,
  placeholder = 'Select recipients',
  className,
  disabled,
}: RecipientSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedSet = new Set(selected.map((e) => e.toLowerCase()))
  const optionValues = new Set(options.map((o) => o.value.toLowerCase()))
  // Custom addresses (typed in, not on the lead) stay listed so they can be unchecked.
  const customSelected = selected.filter((e) => !optionValues.has(e.toLowerCase()))

  const q = query.trim()
  const matches = (text: string) => text.toLowerCase().includes(q.toLowerCase())
  const filtered = q ? options.filter((o) => matches(o.value) || matches(o.label)) : options
  const filteredCustom = q ? customSelected.filter(matches) : customSelected
  const alreadyKnown = optionValues.has(q.toLowerCase()) || selectedSet.has(q.toLowerCase())
  const showAdd = EMAIL_PATTERN.test(q) && !alreadyKnown

  function handleAdd() {
    onToggle(q)
    setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label="Recipient emails"
          className={cn('h-auto min-h-7 justify-start gap-1 py-0.5 font-normal', className)}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selected.map((email) => (
                <Badge key={email} variant="secondary" className="px-1.5 py-0 font-medium">
                  {email}
                </Badge>
              ))
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or type an email…" value={query} onValueChange={setQuery} />
          <CommandList>
            {showAdd && (
              <CommandGroup>
                <CommandItem value={`__add__${q}`} onSelect={handleAdd} className="cursor-pointer">
                  <Plus className="size-4 shrink-0" />
                  <span className="text-muted-foreground">Add</span>
                  <span className="truncate font-medium">{q}</span>
                </CommandItem>
              </CommandGroup>
            )}
            {(filtered.length > 0 || filteredCustom.length > 0) && (
              <CommandGroup>
                {filtered.map((email) => (
                  <CommandItem
                    key={email.value}
                    value={email.value}
                    onSelect={() => onToggle(email.value)}
                    className="cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedSet.has(email.value.toLowerCase())}
                      className="pointer-events-none"
                    />
                    <span className="truncate">{optionLabel(email)}</span>
                  </CommandItem>
                ))}
                {filteredCustom.map((email) => (
                  <CommandItem
                    key={email}
                    value={email}
                    onSelect={() => onToggle(email)}
                    className="cursor-pointer"
                  >
                    <Checkbox checked className="pointer-events-none" />
                    <span className="truncate">{email}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && filteredCustom.length === 0 && !showAdd && (
              <div className="text-muted-foreground py-6 text-center text-sm">
                {q ? 'Keep typing a full email to add it' : 'Type an email to add a recipient'}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
